import { useEffect, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import { handleException } from 'clients/exception-handling'
import { renderTerminalToast } from 'features/settings/features/access/components/bulk-add-job-poller/terminal-notice'
import { TEAM_MEMBERS_QUERY_KEY } from 'features/settings/features/access/hooks/use-team-members'
import {
  BULK_ADD_JOB_CHANGE_EVENT,
  BULK_ADD_JOB_BUDGET_MS,
  BULK_ADD_JOB_STORAGE_KEY,
  type InFlightJob,
  readInFlightJobs,
  removeInFlightJob,
} from 'features/settings/features/access/lib/bulk-add-job-storage'
import { useAccessService } from 'features/settings/features/access/service'

const POLL_INTERVAL_MS = 5_000

/**
 * App-shell-mounted poller that resumes any in-flight bulk-add jobs left
 * in localStorage (across F5 / route changes / multi-tab) and surfaces a
 * sticky toast when each terminates. Renders nothing.
 *
 * Behaviour:
 * - On mount, reads `readInFlightJobs()` (filtered by the 5-min budget)
 *   and starts one poll loop per entry.
 * - Each loop calls `service.fetchBulkJobStatus`; the first non-null
 *   response fires a `toast.success` / `toast.error` (finite autoClose —
 *   the bell notification is the durable surface) and removes the entry
 *   from the storage.
 * - A `storage` event lets a job dispatched in tab A surface its toast
 *   in tab B without polling-tab-coordination.
 *
 * Lives as a Container component (not a hook) so the `useAccessService`
 * call satisfies Bully TypeScript's `no-service-hooks-outside-containers` —
 * service hooks belong in Container.tsx / Root.tsx layers.
 */
export const BulkAddJobPollerContainer = (): JSX.Element => {
  // bully ignore[view-service-hooks: this IS the Container — `view-service-hooks` treats every file under components/ as a View, but Container.tsx is the canonical service-hook call site (see `no-service-hooks-outside-containers`, which only ALLOWS the hook in Container.tsx / Root.tsx). The two rules disagree on this exact site; the latter is the more specific one. permanent: by-design Container.tsx in components/<feature>/ subfolder.]
  const service = useAccessService()
  const queryClient = useQueryClient()
  const activeJobsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(
    new Map(),
  )

  useEffect(() => {
    const startPolling = (job: InFlightJob): void => {
      if (activeJobsRef.current.has(job.jobId)) return
      let attempt = 0
      const maxAttempts = Math.max(
        1,
        Math.ceil(
          (BULK_ADD_JOB_BUDGET_MS - (Date.now() - job.startedAt)) /
            POLL_INTERVAL_MS,
        ),
      )
      const tick = async (): Promise<void> => {
        if (!activeJobsRef.current.has(job.jobId)) return
        attempt += 1
        try {
          const entry = await service.fetchBulkJobStatus(job.hubUuid, job.jobId)
          // `null` is the legacy-404 fallback (pre-deploy pod still
          // raising HTTPException). New protocol returns 200 with
          // status='pending' instead — both mean "keep polling".
          if (entry === null || entry.status === 'pending') {
            if (attempt >= maxAttempts) stop(job.jobId)
            return
          }
          // Race guard: the immediate-fire tick and the interval-fire
          // tick can be in flight simultaneously, and both resolve to a
          // terminal entry once the BG task writes the result. Use the
          // map entry as the "already terminal" gate — first tick to
          // reach here wins, second sees an empty slot and bails.
          if (!activeJobsRef.current.has(job.jobId)) {
            return
          }
          stop(job.jobId)
          renderTerminalToast(job, entry)
          // The finished flow added members server-side; refresh any
          // mounted team-members list (settings > access) so the new
          // rows appear without a manual reload. The prefix key covers
          // the flat + grouped query variants; unmounted queries are
          // just marked stale for their next mount.
          void queryClient.invalidateQueries([TEAM_MEMBERS_QUERY_KEY])
        } catch (cause) {
          handleException(cause, {
            operation: 'settings.access.bulk-add.poll',
            disposition: 'retrying',
            attempt,
            maxAttempts,
            reason: 'Poll again within the five-minute job status budget',
          })
          if (attempt >= maxAttempts) {
            stop(job.jobId)
            toast.error(
              'Could not retrieve the bulk-add result. Check notifications for the job outcome.',
            )
          }
        }
      }
      const handle = setInterval(() => {
        void tick()
      }, POLL_INTERVAL_MS)
      activeJobsRef.current.set(job.jobId, handle)
      // First poll immediately so a fast completion isn't blocked by the
      // initial 5-second wait.
      void tick()
    }

    const stop = (jobId: string): void => {
      const handle = activeJobsRef.current.get(jobId)
      if (handle !== undefined) {
        clearInterval(handle)
        activeJobsRef.current.delete(jobId)
      }
      removeInFlightJob(jobId)
    }

    const reconcile = (): void => {
      const jobs = readInFlightJobs()
      const activeIds = new Set(jobs.map((j) => j.jobId))
      // Stop pollers for entries that have aged out / been removed.
      for (const jobId of Array.from(activeJobsRef.current.keys())) {
        if (!activeIds.has(jobId)) {
          const handle = activeJobsRef.current.get(jobId)
          if (handle !== undefined) clearInterval(handle)
          activeJobsRef.current.delete(jobId)
        }
      }
      // Start pollers for any new entries.
      jobs.forEach(startPolling)
    }

    reconcile()

    const onStorage = (e: StorageEvent): void => {
      if (e.key !== BULK_ADD_JOB_STORAGE_KEY) return
      reconcile()
    }
    // Same-tab bridge — `storage` only fires across OTHER tabs, so a
    // submit-then-poll loop in this tab depends on the custom event the
    // storage util dispatches alongside its writes.
    const onSameTabChange = (): void => {
      reconcile()
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener(BULK_ADD_JOB_CHANGE_EVENT, onSameTabChange)
    const handles = activeJobsRef.current
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(BULK_ADD_JOB_CHANGE_EVENT, onSameTabChange)
      for (const handle of handles.values()) {
        clearInterval(handle)
      }
      handles.clear()
    }
  }, [service, queryClient])

  // Side-effect-only Container — the visible surface is the global
  // ToastContainer mounted in App.tsx. Returning an empty fragment
  // keeps the file in .tsx territory (required by
  // `no-service-hooks-outside-containers`).
  return <></>
}
